import { useEffect, useState } from 'react';
import { History, Loader2, RotateCcw, X } from 'lucide-react';
import useWorkflow, { WorkflowVersionSummary } from './useWorkflow';

interface VersionHistoryPanelProps {
    activationId: number;
    /** Null for the activation's qrScan workflow (legacy routes). */
    workflowId?: number | null;
    onClose: () => void;
    onRestored: () => void;
}

export default function VersionHistoryPanel({
    activationId,
    workflowId = null,
    onClose,
    onRestored,
}: VersionHistoryPanelProps) {
    const { listVersions, restoreVersion } = useWorkflow();
    const [versions, setVersions] = useState<WorkflowVersionSummary[]>([]);
    const [publishedVersionId, setPublishedVersionId] = useState<number | null>(
        null,
    );
    const [isLoading, setIsLoading] = useState(true);
    const [restoringId, setRestoringId] = useState<number | null>(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setIsLoading(true);
            try {
                const result = await listVersions(activationId, workflowId);
                if (cancelled) return;
                setVersions(result.versions);
                setPublishedVersionId(result.publishedVersionId);
            } finally {
                if (!cancelled) setIsLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [activationId, workflowId, listVersions]);

    const handleRestore = async (versionId: number) => {
        setRestoringId(versionId);
        try {
            await restoreVersion(activationId, versionId, workflowId);
            onRestored();
        } finally {
            setRestoringId(null);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30">
            <div className="max-h-[80vh] w-[480px] overflow-auto rounded-lg bg-white p-5 shadow-xl">
                <div className="mb-3 flex items-center justify-between">
                    <span className="flex items-center gap-2 text-base font-semibold text-gray-800">
                        <History size={18} /> Version history
                    </span>
                    <button type="button" onClick={onClose}>
                        <X size={18} className="text-gray-500" />
                    </button>
                </div>

                {isLoading ? (
                    <div className="flex items-center gap-2 py-6 text-gray-500">
                        <Loader2 className="animate-spin" size={16} /> Loading…
                    </div>
                ) : versions.length === 0 ? (
                    <div className="py-6 text-sm text-gray-400">
                        No versions published yet. Use Publish to create the
                        first version.
                    </div>
                ) : (
                    <ul className="space-y-2">
                        {versions.map((v) => (
                            <li
                                key={v.id}
                                className="flex items-center justify-between rounded-md border border-gray-200 p-2"
                            >
                                <div>
                                    <div className="flex items-center gap-2 text-sm font-medium text-gray-800">
                                        Version {v.versionNumber}
                                        {v.id === publishedVersionId && (
                                            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">
                                                LIVE
                                            </span>
                                        )}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                        {new Date(v.createdAt).toLocaleString()}
                                        {v.notes ? ` · ${v.notes}` : ''}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => handleRestore(v.id)}
                                    disabled={restoringId !== null}
                                    className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-100 disabled:opacity-50"
                                >
                                    {restoringId === v.id ? (
                                        <Loader2
                                            size={12}
                                            className="animate-spin"
                                        />
                                    ) : (
                                        <RotateCcw size={12} />
                                    )}
                                    Restore to draft
                                </button>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}
